/**
 * `markdownExtension` — the CodeMirror 6 markdown language + code-block highlighting extension
 * (editor-chat.md §2.1, §5 step 2).
 *
 * editor-chat.md §1.2: CM6 in **markdown source mode** — not a WYSIWYG, not an inline-rendered
 * hybrid. The product is explicitly *not* an authoring innovation (PRD §3); the editor
 * resembles Joplin's. Source mode keeps the markdown body canonical and byte-stable — which is
 * a hard constraint, not a preference: `chunks.hash` change detection (PRD §8.4) and D-007
 * conflict detection both compare bodies (editor-chat.md §1.2).
 *
 * This extension supplies:
 *  - the markdown language parser (the syntax tree the hashtag/wikilink decoration view
 *    plugins read);
 *  - per-language syntax highlighting *inside* fenced code blocks, via `@codemirror/lang-
 *    markdown`'s `codeLanguages` hook fed `@codemirror/language-data`'s registry (editor-chat.md
 *    §2.1 "code-block language injection").
 *
 * It does NOT configure the theme, the line numbers, or the keymaps — those are baseline
 * affordances the `EditorViewHost` composes from `codemirror`'s `basicSetup`. This module is
 * only the language layer.
 */

import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import type { Extension } from '@codemirror/state';

/**
 * The markdown language extension stack — the parser + code-block language injection +
 * syntax highlighting.
 *
 * `base: markdownLanguage` makes GFM the dialect; `codeLanguages: languages` wires the
 * `@codemirror/language-data` registry so a fenced ```` ```python ```` block is highlighted
 * with the Python grammar (lazily loaded). `syntaxHighlighting(defaultHighlightStyle)` applies
 * the default token styling to the markdown source itself (headings, emphasis, links).
 *
 * This is a constant `Extension[]`, not a function — there is nothing per-editor to configure
 * at the language layer; the editor stack just spreads it in.
 */
export const markdownExtension: Extension = [
  markdown({
    base: markdownLanguage,
    // Per-language highlighting inside fenced code blocks (editor-chat.md §2.1). The registry
    // is lazy — a grammar is only loaded when a block of that language appears.
    codeLanguages: languages,
  }),
  // Token styling for the markdown source view (headings, emphasis, link syntax, fence
  // markers). `fallback: true` so any token a theme does not style still gets the default.
  syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
];
